import { el } from './dom';
import { resolveWeatherIconUrl } from './resolveWeatherIconUrl';
import type { WeatherDisplayData, WeatherForecastDay } from './types';

function formatForecastLine(day: WeatherForecastDay): string {
  const parts: string[] = [];
  if (day.date) parts.push(day.date);
  if (day.condition) parts.push(day.condition);
  if (day.highCelsius !== undefined) parts.push(`H:${day.highCelsius}°`);
  if (day.lowCelsius !== undefined) parts.push(`L:${day.lowCelsius}°`);
  return parts.join(' · ');
}

/** Displays whatever normalized fields are present — never decides what's "relevant"; that judgment was already made upstream. */
export function renderWeather(container: HTMLElement, data: WeatherDisplayData | undefined): void {
  const wrapper = el({ tag: 'div', className: 'kia-weather' });
  const hasCurrent = !!data?.current && Object.keys(data.current).length > 0;
  const hasForecast = !!data?.forecast && data.forecast.length > 0;

  if (!data || (!hasCurrent && !hasForecast)) {
    wrapper.append(
      el({ tag: 'p', className: 'kia-weather-empty', text: 'Weather information is unavailable.' }),
    );
    container.append(wrapper);
    return;
  }

  if (data.location?.label) {
    const location = el({ tag: 'p', className: 'kia-weather-location', text: data.location.label });
    location.style.margin = '0';
    wrapper.append(location);
  }

  if (hasCurrent && data.current) {
    const current = el({ tag: 'div', className: 'kia-weather-current' });
    // Each stat is a <span> (see below), which is inline by default — with
    // no layout of its own, the browser runs them together with no space
    // between ("30.4°CSunnyHumidity: 67%..."). display:flex on this parent
    // stacks its children as one-per-line regardless of their own display
    // value, without needing to touch each span individually.
    current.style.display = 'flex';
    current.style.flexDirection = 'column';
    current.style.gap = 'var(--kia-weather-current-gap, 6px)';
    current.style.marginTop = 'var(--kia-weather-current-margin-top, 6px)';

    const iconUrl = resolveWeatherIconUrl(data.current.iconUrl);
    const hasTempOrCondition =
      data.current.temperatureCelsius !== undefined || data.current.condition;
    if (iconUrl || hasTempOrCondition) {
      const headline = el({ tag: 'div', className: 'kia-weather-headline' });
      headline.style.display = 'flex';
      headline.style.alignItems = 'center';
      headline.style.gap = 'var(--kia-weather-headline-gap, 8px)';

      if (iconUrl) {
        const icon = el({
          tag: 'img',
          className: 'kia-weather-icon',
          attrs: { src: iconUrl, alt: '', width: '40', height: '40' },
        });
        icon.style.flexShrink = '0';
        headline.append(icon);
      }

      const text = el({ tag: 'div', className: 'kia-weather-headline-text' });
      text.style.display = 'flex';
      text.style.flexDirection = 'column';
      if (data.current.temperatureCelsius !== undefined) {
        text.append(
          el({
            tag: 'span',
            className: 'kia-weather-temp',
            text: `${data.current.temperatureCelsius}°C`,
          }),
        );
      }
      if (data.current.condition) {
        text.append(
          el({ tag: 'span', className: 'kia-weather-condition', text: data.current.condition }),
        );
      }
      headline.append(text);
      current.append(headline);
    }

    if (data.current.humidityPercent !== undefined) {
      current.append(
        el({
          tag: 'span',
          className: 'kia-weather-humidity',
          text: `Humidity: ${data.current.humidityPercent}%`,
        }),
      );
    }
    if (data.current.windSpeedKph !== undefined) {
      current.append(
        el({
          tag: 'span',
          className: 'kia-weather-wind',
          text: `Wind: ${data.current.windSpeedKph} km/h`,
        }),
      );
    }
    wrapper.append(current);
  }

  if (hasForecast && data.forecast) {
    const list = el({ tag: 'ul', className: 'kia-weather-forecast' });
    // A <ul>'s default bullet + left padding reads as a stray list rather
    // than part of the weather card — this widget only ever displays
    // forecast lines, never something that benefits from bullets.
    list.style.listStyle = 'none';
    list.style.margin = '0';
    list.style.padding = '0';
    list.style.marginTop = 'var(--kia-weather-forecast-margin-top, 8px)';
    list.style.display = 'flex';
    list.style.flexDirection = 'column';
    list.style.gap = 'var(--kia-weather-forecast-gap, 4px)';
    for (const day of data.forecast) {
      list.append(el({ tag: 'li', text: formatForecastLine(day) }));
    }
    wrapper.append(list);
  }

  container.append(wrapper);
}
